import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { Row, Col, Button, Typography, message, Input, Select as AntSelect } from 'antd'
import { LinkOutlined, CopyOutlined, CheckOutlined } from '@ant-design/icons'
import useAction from 'utils/useAction'
import { selectors as arcAdsSelectors } from 'domains/arcAds'
import * as arcAdsActions from 'actions/views/arcAds'
import { clearConvertedUrl } from 'domains/arcAds/actions'
import {
  ConverterSection,
  ConverterButton,
  ConvertButton,
  ResultSection,
  CopyButton,
  ErrorText
} from './LinkConverter.styles'

const { Text } = Typography

const LinkConverter = ({ onUrlConverted }) => {
  const [activeKey, setActiveKey] = useState([])
  const [inputLink, setInputLink] = useState('')
  const [selectedNetwork, setSelectedNetwork] = useState('')
  const [copied, setCopied] = useState(false)

  const affiliateNetworks = useSelector(arcAdsSelectors.getAffiliateNetworks)
  const isNetworksLoaded = useSelector(arcAdsSelectors.getIsNetworksLoaded)
  const isConverting = useSelector(arcAdsSelectors.getIsConverting)
  const convertedUrl = useSelector(arcAdsSelectors.getConvertedUrl)
  const converterError = useSelector(arcAdsSelectors.getConverterError)
  const fetchNetworks = useAction(arcAdsActions.fetchAffiliateNetworks)
  const convertUrl = useAction(arcAdsActions.convertClickUrl)
  const clearConverted = useAction(clearConvertedUrl)

  useEffect(() => {
    if (!isNetworksLoaded) {
      fetchNetworks()
    }
  }, [isNetworksLoaded, fetchNetworks])

  const networkOptions = (affiliateNetworks || []).map(network => ({
    value: network,
    label: network
  }))

  const handleToggle = () => {
    const newKey = activeKey.length > 0 ? [] : ['1']
    setActiveKey(newKey)
    if (newKey.length === 0) {
      clearConverted()
      setInputLink('')
      setSelectedNetwork('')
      setCopied(false)
    }
  }

  const validateUrl = (url) => {
    if (!url || url.trim() === '') {
      return 'URL is required'
    }
    const urlPattern = /^https?:\/\/.+\..+/
    if (!urlPattern.test(url)) {
      return 'Please enter a valid URL'
    }
    return undefined
  }

  const handleConvert = () => {
    const urlError = validateUrl(inputLink)
    if (urlError) {
      message.error(urlError)
      return
    }

    convertUrl({
      link: inputLink.trim(),
      network: selectedNetwork || undefined
    })
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(convertedUrl)
      setCopied(true)
      message.success('URL copied to clipboard')
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      message.error('Failed to copy URL')
    }
  }

  const handleUseUrl = () => {
    if (convertedUrl && onUrlConverted) {
      onUrlConverted(convertedUrl)
      message.success('URL applied to Click URL field')
    }
  }

  return (
    <ConverterSection>
      <ConverterButton
        icon={<LinkOutlined />}
        onClick={(e) => {
          e.preventDefault()
          handleToggle()
        }}
        type="link"
      >
        Link Converter
      </ConverterButton>

      {activeKey.length > 0 && (
        <div style={{ marginTop: '12px' }}>
          <b>How It Works</b>
          <br />
          <b>Without network specified:</b> Returns the original link with proper URL encoding
          <br />
          <b>With network specified:</b> Adds the network-specific UID parameter if the network is supported
          <br />
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                Network (Optional)
              </Text>
              <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                Select an affiliate network for tracking
              </Text>
              <AntSelect
                value={selectedNetwork}
                onChange={setSelectedNetwork}
                options={networkOptions}
                placeholder="Select a network"
                allowClear
                showSearch
                filterOption={(input, option) => {
                  if (!input) return true
                  const label = option?.label || ''
                  return label.toLowerCase().includes(input.toLowerCase())
                }}
                style={{ width: '100%' }}
              />
            </Col>
            <Col span={12}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                URL to Convert *
              </Text>
              <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                Enter the affiliate URL you want to convert
              </Text>
              <Input
                value={inputLink}
                onChange={(e) => setInputLink(e.target.value)}
                placeholder="https://example.com/product"
                style={{ width: '100%' }}
              />
            </Col>
            <Col span={24}>
              <ConvertButton
                type="primary"
                onClick={handleConvert}
                loading={isConverting}
                disabled={!inputLink.trim() || isConverting}
              >
                {isConverting ? 'Converting...' : 'Convert URL'}
              </ConvertButton>
            </Col>
            {convertedUrl && (
              <Col span={24}>
                <ResultSection>
                  <Text strong style={{ display: 'block', marginBottom: 8 }}>
                    Converted URL:
                  </Text>
                  <div style={{
                    padding: '8px 12px',
                    backgroundColor: '#f6f6f6',
                    borderRadius: '6px',
                    marginBottom: '12px',
                    wordBreak: 'break-all',
                    fontSize: '13px'
                  }}>
                    {convertedUrl}
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <CopyButton
                      icon={copied ? <CheckOutlined /> : <CopyOutlined />}
                      onClick={handleCopy}
                      size="small"
                    >
                      {copied ? 'Copied!' : 'Copy'}
                    </CopyButton>
                    <Button
                      type="primary"
                      onClick={handleUseUrl}
                      size="small"
                    >
                      Use This URL
                    </Button>
                  </div>
                </ResultSection>
              </Col>
            )}

            {converterError && (
              <Col span={24}>
                <ErrorText>{converterError}</ErrorText>
              </Col>
            )}
          </Row>
        </div>
      )}
    </ConverterSection>
  )
}

LinkConverter.propTypes = {
  onUrlConverted: PropTypes.func,
}

export default LinkConverter